import {
  BRAND_AFFINITY_DISPLAY,
  BRAND_AFFINITY_FIELD,
} from 'modules/journey/components/FilterInputsList/FilterInputsList.constants';
import { Segment } from 'modules/messagingInsights/components/Audience/types';
import { Metric } from 'modules/messagingInsights/types';
import { MutableRefObject } from 'react';
import { TFunction } from 'react-i18next';
import { CURRENCY, NUMBER, PERCENTAGE } from 'ui/card/constants';
import { BaseOption, SelectValueType } from 'ui/selects';
import { ColumnData } from 'ui/tableHeader';
import { sentenceCase } from 'utils/stringHelpers';
import { currencySet, percentSet } from '../messagingInsights/constants';
import { MetricDefinition } from './types';

type DropdownValue<TValue extends SelectValueType = any> = BaseOption<TValue>[];

type CSVHeader = {
  label: string;
  key: string;
};

export const parseField = (field: Segment = {}) => {
  if (field.name === BRAND_AFFINITY_FIELD) {
    return {
      ...field,
      name: BRAND_AFFINITY_DISPLAY,
    };
  }
  return field;
};

export const parseUserMappingsForDropdown = (userMappings: Segment[] = []) => {
  const dropdownArr: DropdownValue = [];
  const sorted = userMappings.sort((a, b) => {
    const fieldA = parseField(a);
    const fieldB = parseField(b);

    if (fieldA && fieldB) {
      if (fieldA < fieldB) {
        return -1;
      } else if (fieldB < fieldA) {
        return 1;
      }
    }
    return 0;
  });

  sorted.forEach((val, idx) => {
    // hide user properties of the geo_location type until BE supports them
    if (val.type !== 'geo_location') {
      dropdownArr.push({
        badgeText: '',
        value: val,
        id: `userMapping-${val.name}-${idx}`,
        doubleLine: val.type,
        label: val.name,
      });
    }
  });
  return dropdownArr;
};

export const isPercentage = (valueName: string) => {
  const valueNameLowerCase = valueName.toLowerCase();
  return (
    valueNameLowerCase.includes('rate') ||
    percentSet.has(valueNameLowerCase) ||
    valueNameLowerCase.startsWith('conversions /')
  );
};

export const getNumberType = (valueName: string) => {
  if (isPercentage(valueName)) {
    return PERCENTAGE;
  }
  if (valueName.toLowerCase().includes('revenue') || currencySet.has(valueName.toLowerCase())) {
    return CURRENCY;
  }
  return NUMBER;
};

export const formatNumber = (valueName: string, value: any) => {
  if (value === undefined) {
    return 'N/A';
  }
  if (typeof value === 'number') {
    const numType = getNumberType(valueName);
    if (numType === PERCENTAGE) {
      return `${(value * 100).toFixed(2)}%`;
    } else if (numType === CURRENCY) {
      return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else {
      return value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
    }
  }
  return value;
};

export const formatNumberGivenValueType = (valueType: string, value: any) => {
  if (value === undefined) {
    return 'N/A';
  }
  if (typeof value === 'number') {
    if (valueType === PERCENTAGE) {
      return `${(value * 100).toFixed(2)}%`;
    } else if (valueType === CURRENCY) {
      return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else {
      return value.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });
    }
  }
  return value;
};

export const tooltipValue = (valueName: string, value: number) => {
  const valueType = getNumberType(valueName);
  const numberOfDecimals = value?.toString()?.split('.')?.[1]?.length || 0;
  if (numberOfDecimals > 2) {
    if (valueType === PERCENTAGE) {
      return (value * 100).toString();
    } else if (valueType === CURRENCY) {
      return `$${value.toString}`;
    } else {
      return value.toLocaleString(undefined, { minimumFractionDigits: numberOfDecimals });
    }
  }
  return null;
};

// Safe
/**
 * @param metricLabel 'Total SMS Sent'
 * @returns Sentence case version of the label ('Total SMS sent')
 */
export const metricSentenceCase = (metricLabel: string) => {
  if (!metricLabel?.length) {
    return '';
  }

  const exceptions = new Set(['M', 'SMS', '(SMS)', 'WhatsApp']);
  const words = metricLabel.split(' ');
  const firstWord = exceptions.has(words[0]) ? words[0] : sentenceCase(words[0]);
  return words
    .slice(1)
    .reduce(
      (accStr, word) => `${accStr} ${exceptions.has(word) ? word : word.toLowerCase()}`,
      firstWord,
    );
};

export const parseColsToCSVHeaders = (cols: ColumnData[]) => {
  const headerArr: CSVHeader[] = [];
  cols.forEach((col) => {
    headerArr.push({
      label: typeof col.name === 'string' ? col.name : metricSentenceCase(col.value),
      key: col.value,
    });
  });
  return headerArr;
};

/**
 * Sort an array based on its items' ordering in another array
 * e.g. Given an unsorted array ['Total Push Sends', 'Total Email Sends', 'Total Conversion Rate'],
 * which you want sorted according to the ordering in this array:
 * ['Total Email Sends', 'Total Email Opens', ...  'Total Conversion Rate', 'Total Push Sends', 'Total Unsubscribes']
 * this function will return the newly sorted array ['Total Email Sends', 'Total Conversion Rate', 'Total Push Sends']
 */
export const sortBySelectionOrder = <T>(correctlySortedArray: T[], unsortedArray: T[]) => {
  const arrCopy = [...unsortedArray];
  arrCopy?.sort((a, b) => correctlySortedArray.indexOf(a) - correctlySortedArray.indexOf(b));
  return arrCopy;
};

export const formatDeviceData = (deviceData: Record<string, number>) => {
  const getIcon = (device: string) => {
    switch (device) {
      case 'Mac':
        return 'fa-apple';
      case 'Linux':
        return 'fa-linux';
      case 'Gmail':
        return 'fa-google';
      case 'iPhone':
        return 'fa-mobile';
      case 'Android':
        return 'fa-android';
      case 'Windows':
        return 'fa-windows';
      case 'iPad':
        return 'fa-apple';
      default:
        return '';
    }
  };

  return Object.keys(deviceData)
    ?.sort((deviceA, deviceB) => deviceData[deviceB] - deviceData[deviceA])
    .map((deviceName) => ({
      y: deviceData[deviceName],
      name: `<i class="fa ${getIcon(deviceName)}"></i> ${
        deviceName === '-' ? 'Unknown' : deviceName
      }`,
    }));
};

export const isSMSClickMetric = (metricName: string) => {
  const lower = metricName.toLowerCase();
  return (
    lower === 'total sms clicks' || lower === 'unique sms clicks' || lower === 'sms click rate'
  );
};

/* Chart actions */

export const printChart = (chart: MutableRefObject<any>) => {
  chart?.current?.chart?.print();
};

export const downloadPNG = (chart: MutableRefObject<any>) => {
  chart?.current?.chart?.exportChart();
};

export const downloadJPEG = (chart: MutableRefObject<any>) => {
  chart?.current?.chart?.exportChart({ type: 'image/jpeg' });
};

export const downloadPDF = (chart: MutableRefObject<any>) => {
  chart?.current?.chart?.exportChart({ type: 'application/pdf' });
};

export const downloadSVG = (chart: MutableRefObject<any>) => {
  chart?.current?.chart?.exportChart({ type: 'image/svg+xml' });
};

export const getChartExportingOptions = (id: string, ct?: TFunction) => {
  const translatedLabel = (key: string) => {
    return ct ? ct(`chartExportOptions.${key.replace(/ /g, '_')}`) : key;
  };

  return [
    { label: translatedLabel('Print chart'), id: `${id}-chart-printChart` },
    { label: translatedLabel('Download PNG image'), id: `${id}-chart-downloadPNG` },
    { label: translatedLabel('Download JPEG image'), id: `${id}-chart-downloadJPEG` },
    { label: translatedLabel('Download PDF document'), id: `${id}-chart-downloadPDF` },
    { label: translatedLabel('Download SVG vector image'), id: `${id}-chart-downloadSVG` },
  ];
};

export const getChartActions = (id: string, chart: MutableRefObject<any>) => {
  return {
    [`${id}-chart-printChart`]: () => printChart(chart),
    [`${id}-chart-downloadPNG`]: () => downloadPNG(chart),
    [`${id}-chart-downloadJPEG`]: () => downloadJPEG(chart),
    [`${id}-chart-downloadPDF`]: () => downloadPDF(chart),
    [`${id}-chart-downloadSVG`]: () => downloadSVG(chart),
  };
};

export const pluralize = (str: string, length: number) => (length !== 1 ? `${str}s` : str);

export const parseMetricValueTypeMap = (metrics: Metric[]): Record<string, string> =>
  metrics.reduce((agg: Record<string, string>, metric: Metric) => {
    agg[metric.metricNiceName] = metric.valueType;
    return agg;
  }, {});

export const parseMetricDefinition = (
  metrics: Metric[],
  metricDefinitions: MetricDefinition[],
): Record<string, MetricDefinition> =>
  metrics.reduce((agg: Record<string, MetricDefinition>, metric: Metric) => {
    const metricDefinition = metricDefinitions.find(
      (metDef) => metric.metricName === metDef.metricName,
    );
    if (metricDefinition) {
      agg[metric.metricNiceName.toLowerCase()] = metricDefinition;
    }
    return agg;
  }, {});
